"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import type { Session } from "next-auth";
import { useSession } from "next-auth/react";

import { errorIntoString } from "@/lib/utils";
import { formatServiceDisplayTitle } from "@/lib/utils/service-title";
import type { CategoryFilterNavRow } from "@/lib/utils/public-category-listing-payload";
import type { Service, ServicesApiResponse } from "@/types";
import { buildListItemFromService, ga4SaveItem, ga4ViewItemList } from "@/lib/analytics/ga4-ecommerce";
import { useCurrency } from "@/lib/contexts/CurrencyContext";
import {
	useGetSavedServicesQuery,
	useSetSavedServicesMutation,
} from "@/features/saved/savedApiInjections";

import { useToast } from "@/components/ui/use-toast";
import SignupPromptModal from "@/components/auth/SignupPromptModal";
import ServiceCard from "@/components/cards/ServiceCard";
import SortByDropdown from "@/components/menu/SortByDropdown";
import { SERVICE_SORTS } from "@/components/menu/SortByDropdown/sorts";
import FilterPopup from "@/app/categories/[categoryId]/components/FilterPopup";
import ListingToolbarSearchPill from "@/app/categories/[categoryId]/components/ListingToolbarSearchPill";
import PaginationBar from "@/components/PaginationBar";

type Props = {
	data: ServicesApiResponse;
	categoryTitle: string;
	/** When set with nav arrays, filter sheet includes Category / Industry (listing pages only). */
	filterMainCategory?: CategoryFilterNavRow;
	filterActiveSubcategory?: CategoryFilterNavRow | null;
	/** URL-driven main selection (defaults to path main ± `categories` query). */
	filterSelectedMainIds?: string[];
	/** URL-driven sector selection (defaults from path sub + `subcategoryIds` query). */
	filterSelectedSubcategoryIds?: string[];
	filterNavMainCategories?: CategoryFilterNavRow[];
	filterNavSubcategories?: CategoryFilterNavRow[];
	sectorIdsByMainId?: Record<string, string[]>;
	/** Apply category/sector filters on the current path (search results). */
	filterStayOnCurrentPath?: boolean;
	showFilters?: boolean;
	/** @deprecated Session is resolved on the client via `useSession` for edge-cacheable pages. */
	session?: Session | null;
	/** When set with `itemListName`, fires `view_item_list` when the result set changes */
	itemListId?: string;
	itemListName?: string;
};

export default function ServiceList({
	data,
	session: sessionProp,
	categoryTitle,
	filterMainCategory,
	filterActiveSubcategory = null,
	filterSelectedMainIds: filterSelectedMainIdsProp,
	filterSelectedSubcategoryIds: filterSelectedSubcategoryIdsProp,
	filterNavMainCategories,
	filterNavSubcategories,
	sectorIdsByMainId,
	filterStayOnCurrentPath = false,
	showFilters = true,
	itemListId,
	itemListName,
}: Props) {
	const { data: clientSession } = useSession();
	const session = sessionProp !== undefined ? sessionProp : (clientSession ?? null);
	const { toast } = useToast();
	const currency = useCurrency();

	const { data: saved, isLoading: savedServicesLoading } = useGetSavedServicesQuery(
		session?.user.id ?? "",
		{
			skip: !session,
		},
	);
	const [setFav] = useSetSavedServicesMutation();
	const [favoriteBusyServiceId, setFavoriteBusyServiceId] = useState<string | null>(null);
	const [showSignupModal, setShowSignupModal] = useState(false);
	const [signupReturnUrl, setSignupReturnUrl] = useState<string | undefined>(undefined);
	/**
	 * RTK `isLoading` is false on the server SSR pass but true on the first client paint when a
	 * session exists — that flips `disabled` on the heart and causes a hydration mismatch.
	 * Only surface loading UI after mount so server HTML and the first client render match.
	 */
	const [favoritesHydrated, setFavoritesHydrated] = useState(false);
	useEffect(() => {
		setFavoritesHydrated(true);
	}, []);
	const favoriteListLoading = favoritesHydrated && savedServicesLoading;

	const listDocKey = useMemo(
		() => `${data.page}:${data.docs.map((d) => d._id).join(",")}`,
		[data.page, data.docs],
	);

	useEffect(() => {
		if (!itemListId || !itemListName || !data.docs.length) return;
		const items = data.docs.map((svc, i) => buildListItemFromService(svc, currency, i + 1));
		ga4ViewItemList({ item_list_id: itemListId, item_list_name: itemListName, items });
	}, [itemListId, itemListName, listDocKey, currency, data.docs]);

	const handleFavoriteClick = useCallback(
		async (service: Service) => {
			if (!session) {
				setSignupReturnUrl(
					typeof window === "undefined"
						? undefined
						: `${window.location.pathname}${window.location.search}`,
				);
				setShowSignupModal(true);
				return;
			}

			if (saved === undefined) return;
			const displayTitle = formatServiceDisplayTitle(service.title);
			setFavoriteBusyServiceId(service._id);
			try {
				const isFavorite = saved.serviceIds.find((x) => x._id === service._id);

				if (isFavorite) {
					const resp = await setFav({
						userId: session.user.id,
						serviceIds: saved.serviceIds
							.filter((x) => x._id !== service._id)
							.map((x) => x._id),
					});

					if ("error" in resp) {
						const ex = errorIntoString(resp.error);
						toast({
							variant: "destructive",
							title: `Failed to remove ${displayTitle} from saved`,
							description: ex,
						});

						return;
					}

					toast({ title: `${displayTitle} removed from saved` });
					ga4SaveItem({
						content_type: "service",
						item_id: service._id,
						item_name: displayTitle,
						saved: false,
					});
					return;
				}

				const resp = await setFav({
					userId: session.user.id,
					serviceIds: [...saved.serviceIds.map((id) => id._id), service._id],
				});

				if ("error" in resp) {
					const ex = errorIntoString(resp.error);
					toast({
						variant: "destructive",
						title: `Failed to add ${displayTitle} to saved`,
						description: ex,
					});
					return;
				}

				toast({ title: `${displayTitle} saved` });
				ga4SaveItem({
					content_type: "service",
					item_id: service._id,
					item_name: displayTitle,
					saved: true,
				});
			} finally {
				setFavoriteBusyServiceId(null);
			}
		},
		[session, saved, setFav, toast]
	);

	if (!data || !Array.isArray(data.docs)) {
		return (
			<div className="container flex min-h-64 flex-col items-center justify-center">
				<h1 className="text-2xl font-semibold">Unable to load services</h1>
			</div>
		);
	}

	const hasDocs = data.docs.length > 0;

	const filterSelectedMainIds =
		filterSelectedMainIdsProp ??
		(filterMainCategory ? [String(filterMainCategory._id)] : []);
	const filterSelectedSubcategoryIds =
		filterSelectedSubcategoryIdsProp ??
		(filterActiveSubcategory ? [String(filterActiveSubcategory._id)] : []);

	return (
		<div className="container flex flex-col gap-y-6 pb-8 pt-4 sm:gap-y-2 sm:pt-0">
			{showFilters && (
				<div className="grid grid-cols-[1fr] items-center gap-y-4 sm:grid-cols-[1fr_auto] sm:grid-rows-[5rem] sm:gap-x-2 sm:gap-y-0">
					<span className="text-sm sm:self-center">
						Showing {data.docs.length} of {data.totalDocs} results
					</span>
					<div className="flex h-10 w-full min-w-0 items-center justify-end gap-2 sm:w-auto sm:self-center">
						<ListingToolbarSearchPill />
						<div className="shrink-0">
							<SortByDropdown
								defaultSort="default"
								removeParamWhen="default"
								sorts={SERVICE_SORTS}
								glassToolbar
							/>
						</div>
						<div className="shrink-0">
							<FilterPopup
								categoryTitle={categoryTitle}
								filterMainCategory={filterMainCategory}
								filterSelectedMainIds={filterSelectedMainIds}
								filterSelectedSubcategoryIds={filterSelectedSubcategoryIds}
								filterNavMainCategories={filterNavMainCategories}
								filterNavSubcategories={filterNavSubcategories}
								sectorIdsByMainId={sectorIdsByMainId}
								filterStayOnCurrentPath={filterStayOnCurrentPath}
							/>
						</div>
					</div>
				</div>
			)}

			{hasDocs ? (
				<div className="mb-4 grid grid-cols-1 justify-center gap-8 sm:grid-cols-2 lg:grid-cols-[repeat(auto-fill,18rem)] xl:gap-x-4 2xl:grid-cols-[repeat(auto-fill,20rem)] 2xl:gap-x-8">
					{data.docs.map((x, index) => (
						<ServiceCard
							key={x._id}
							service={x}
							mediaPriority={index < 6}
							listing={
								itemListId && itemListName
									? { itemListId, itemListName, index: index + 1 }
									: undefined
							}
							favorite={{
								isLoading: favoriteListLoading,
								isUpdating: favoriteBusyServiceId === x._id,
								isFavorite:
									saved === undefined
										? false
										: saved.serviceIds.findIndex((z) => z._id === x._id) !== -1,
								onClick: handleFavoriteClick,
							}}
						/>
					))}
				</div>
			) : (
				<div className="flex min-h-48 flex-col items-center justify-center py-12">
					<h2 className="text-2xl font-semibold">No Services Available</h2>
				</div>
			)}

			{data.totalPages > 1 ? <PaginationBar totalPages={data.totalPages} /> : null}
			<SignupPromptModal
				open={showSignupModal}
				onOpenChange={setShowSignupModal}
				returnUrl={signupReturnUrl}
				title="Sign up to save this service"
				description="Create a Techxeed account before saving this service."
			/>
		</div>
	);
}
